import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Coach from "@/pages/Coach";
import Challenges from "@/pages/Challenges";
import Shop from "@/pages/Shop";
import Wearables from "@/pages/Wearables";
import Squad from "@/pages/Squad";
import Orders from "@/pages/Orders";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";

function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = React.useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/", { replace: true });
      return;
    }
    const session_id = decodeURIComponent(m[1]);
    (async () => {
      try {
        const r = await api.post("/auth/emergent-session", { session_id });
        localStorage.setItem("fslab_session_token", r.data.session_token);
        await refresh();
        window.history.replaceState({}, document.title, "/app");
        navigate("/app", { replace: true, state: { user: r.data.user } });
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="eyebrow mb-2">FS LAB</div>
        <div className="font-display text-4xl">Signing you in…</div>
        {error && <div className="mt-4 text-sm text-red-600">{error}</div>}
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // AuthCallback hands off the user via location.state to sidestep any state-timing race.
  if (location.state?.user) return children;
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="eyebrow">LOADING…</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }
  return children;
}

function AppRouter() {
  // Detect session_id during render BEFORE any other routing (Google flow)
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // WHOOP-signin flow: /app?whoop_signin=<session_token>
  if (typeof window !== "undefined" && window.location.search?.includes("whoop_signin=")) {
    return <WhoopSigninCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="coach" element={<Coach />} />
        <Route path="challenges" element={<Challenges />} />
        <Route path="shop" element={<Shop />} />
        <Route path="orders" element={<Orders />} />
        <Route path="wearables" element={<Wearables />} />
        <Route path="squad" element={<Squad />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function WhoopSigninCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const processed = React.useRef(false);
  React.useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams(window.location.search);
    const token = params.get("whoop_signin");
    if (!token) { navigate("/", { replace: true }); return; }
    localStorage.setItem("fslab_session_token", token);
    // Also set as cookie via a lightweight /auth/me call which will re-issue cookie
    (async () => {
      await refresh();
      window.history.replaceState({}, document.title, "/app");
      navigate("/app", { replace: true, state: { user: { via: "whoop" } } });
    })();
  }, [navigate, refresh]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <div className="eyebrow mb-2">FS LAB · WHOOP</div>
        <div className="font-display text-4xl">Syncing your WHOOP…</div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </AuthProvider>
  );
}
